/* global FB */
import React, { useState, useEffect } from 'react';
import { Loader, LogIn, Sparkles } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function LoginScreen({ onLoginSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fbStatus, setFbStatus] = useState('loading');
  const [userInfo, setUserInfo] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);

  // Initialize Facebook SDK
  useEffect(() => {
    initializeFacebookSDK();
  }, []);

  const initializeFacebookSDK = () => {
    // Always try to load Facebook SDK
    // (Removed HTTP check - let SDK handle it)

    // Check if FB SDK is already loaded
    if (window.fbAsyncInit) {
      return;
    }

    window.fbAsyncInit = function () {
      FB.init({
        appId: '1639071547433907',
        xfbml: true,
        version: 'v25.0'
      });

      // Check login status
      FB.getLoginStatus(function (response) {
        if (response.status === 'connected') {
          setFbStatus('connected');
        } else {
          setFbStatus('notConnected');
        }
      });
    };

    // Load the SDK dynamically
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    document.body.appendChild(script);
  };

  const handleFacebookLogin = () => {
    if (!window.FB) {
      setError('Facebook SDK not loaded. Please wait and try again.');
      return;
    }

    setLoading(true);
    setError(null);

    FB.login(
      function (response) {
        if (response.authResponse) {
          const userAccessToken = response.authResponse.accessToken;

          // Get user info
          FB.api(
            '/me',
            { access_token: userAccessToken, fields: 'name,email,picture' },
            function (userInfoResponse) {
              // Get user's pages (with their page access tokens)
              FB.api(
                '/me/accounts',
                { access_token: userAccessToken, fields: 'id,name,category,access_token,picture' },
                function (pagesResponse) {
                  if (pagesResponse.data && pagesResponse.data.length > 0) {
                    // Store user info and all pages, show page selection
                    setUserInfo(userInfoResponse);
                    setPages(pagesResponse.data);
                    setSelectedPageIndex(0);
                    setLoading(false);
                  } else {
                    setError('No Facebook pages found. Please create a page first.');
                    setLoading(false);
                  }
                }
              );
            }
          );
        } else {
          setError('Facebook login was cancelled.');
          setLoading(false);
        }
      },
      { scope: 'pages_read_engagement,pages_manage_metadata,pages_read_user_content,public_profile,email' }
    );
  };

  const handleConfirmPage = async () => {
    if (!userInfo || !pages[selectedPageIndex]) {
      setError('Page selection failed.');
      return;
    }

    try {
      setLoading(true);
      const selectedPage = pages[selectedPageIndex];
      const pageAccessToken = selectedPage.access_token;
      
      // Validate the page access token with backend
      // This checks permissions and verifies the token is valid for posting comments
      console.log('[LOGIN] Validating page access token...');
      
      const response = await fetch(
        `${API_BASE_URL}/providers/facebook/page-access-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pageAccessToken: pageAccessToken,  // Pass the page token from SDK
            pageId: selectedPage.id
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[LOGIN] Validation error:', errorData);
        setError(`Failed to validate token: ${errorData.error || 'Unknown error'}`);
        setLoading(false);
        return;
      }

      const tokenData = await response.json();
      console.log('[LOGIN] ✓ Page access token validated');
      
      // Use the validated page access token
      const validatedToken = tokenData.pageAccessToken;

      // Save to both localStorage (persistent) and sessionStorage (immediate access)
      const session = {
        user: userInfo,
        page: selectedPage,
        pageAccessToken: validatedToken,
        timestamp: Date.now()
      };
      localStorage.setItem('repugate_session', JSON.stringify(session));
      sessionStorage.setItem('repugate_session', JSON.stringify(session));

      // Register user and page in Supabase (non-blocking - don't wait)
      console.log('[LOGIN] Registering user and page in database...');
      fetch(`${API_BASE_URL}/providers/register-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userInfo.id,
          email: userInfo.email,
          name: userInfo.name,
          pageId: selectedPage.id,
          pageName: selectedPage.name
        })
      })
        .then(r => r.json())
        .then(data => console.log('[LOGIN] ✓ Registration response:', data))
        .catch(err => console.warn('[LOGIN] Registration warning:', err.message));

      setLoading(false);
      
      // Callback to parent
      onLoginSuccess({
        user: userInfo,
        page: selectedPage,
        accessToken: validatedToken
      });
    } catch (err) {
      console.error('[LOGIN] Error validating token:', err);
      setError(`Failed to complete login: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl border border-purple-500/20 rounded-3xl shadow-2xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <Sparkles className="w-14 h-14 text-pink-400 mx-auto mb-4" />
          <h1 className="text-4xl font-black bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text mb-2">
            RepuGate
          </h1>
          <p className="text-purple-300/80">
            AI-Powered Facebook Review Manager
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg backdrop-blur-sm">
            <p className="text-red-200 text-sm">⚠️ {error}</p>
          </div>
        )}

        {/* Show page selection if user is logged in */}
        {pages.length > 0 ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-purple-200 mb-3">
                Select Facebook Page
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {pages.map((page, index) => (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPageIndex(index)}
                    className={`w-full p-4 rounded-xl text-left transition-all border ${
                      selectedPageIndex === index
                        ? 'bg-gradient-to-r from-purple-600/40 to-pink-600/40 border-purple-500/60 ring-2 ring-purple-500/30'
                        : 'bg-slate-700/30 border-purple-500/20 hover:border-purple-500/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {page.picture?.data?.url && (
                        <img 
                          src={page.picture.data.url} 
                          alt={page.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{page.name}</p>
                        <p className="text-purple-300/60 text-xs truncate">{page.category || 'Page'}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={handleConfirmPage}
              className="w-full py-3 px-4 rounded-xl font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 active:scale-95 shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <Sparkles size={18} />
              Continue with {pages[selectedPageIndex]?.name || 'Selected Page'}
            </button>

            <button
              onClick={() => {
                setPages([]);
                setUserInfo(null);
                setError(null);
              }}
              className="w-full py-2.5 text-purple-200 border border-purple-500/30 rounded-lg hover:border-purple-500/60 hover:bg-slate-700/30 transition-all"
            >
              ← Choose Different Account
            </button>
          </div>
        ) : (
          /* Show Facebook login button */
          <div className="space-y-4">
            <button
              onClick={handleFacebookLogin}
              disabled={loading || fbStatus === 'loading'}
              className={`w-full py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all ${
                loading
                  ? 'bg-purple-600/50 text-purple-200 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 active:scale-95 shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <LogIn size={20} />
                  Sign In with Facebook
                </>
              )}
            </button>

            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl backdrop-blur-sm">
              <p className="text-xs text-blue-200">
                <strong className="text-blue-100">First time?</strong> Make sure your Facebook page has the Ratings feature enabled.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-purple-300/60">
            RepuGate • AI-Powered Facebook Review Management
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginScreen;
